"use client";

import { useState } from "react";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface PlanProps {
  title: string;
  popular: boolean;
  monthlyPrice: number;
  annualPrice: number;
  lifetimePrice?: number;
  buttonLabel?: string;
  lifeTimeButtonLabel?: string;
  features: string[];
  monthlyPriceId: string;
  annualPriceId: string;
  lifetimePriceId?: string;
  description: string;
}

const plans: PlanProps[] = [
  {
    title: "Starter",
    description: "Perfect for individuals and small projects",
    monthlyPrice: 19,
    annualPrice: 190,
    annualPriceId: "",
    monthlyPriceId: "",
    buttonLabel: "Get started",
    lifetimePrice: 499,
    lifetimePriceId: "demo",
    lifeTimeButtonLabel: "Purchase Now",
    features: [
      "Up to 3 projects",
      "Basic analytics",
      "Email support",
      "1 team member",
      "5GB storage",
    ],
    popular: false,
  },
  {
    title: "Pro",
    description: "Everything you need for growing businesses",
    monthlyPrice: 49,
    annualPrice: 490,
    annualPriceId: "",
    monthlyPriceId: "",
    buttonLabel: "Get started",
    lifetimePrice: 499,
    lifetimePriceId: "demo",
    lifeTimeButtonLabel: "Purchase Now",
    features: [
      "Unlimited projects",
      "Advanced analytics",
      "Priority support",
      "Up to 10 team members",
      "50GB storage",
      "API access",
      "Custom integrations",
    ],
    popular: true,
  },
  {
    title: "Enterprise",
    description: "Everything you need for growing businesses",
    monthlyPrice: 49,
    annualPrice: 490,
    annualPriceId: "",
    monthlyPriceId: "",
    buttonLabel: "Get started",
    features: [
      "Unlimited everything",
      "Enterprise analytics",
      "Dedicated support",
      "Unlimited team members",
      "500GB storage",
      "Advanced security",
      "Custom branding",
      "SSO authentication",
    ],
    popular: false,
  },
];

export default function Pricing07() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "annual"
  );

  function PricingCard({ plan }: { plan: PlanProps }) {
    return (
      <div
        className={`relative rounded-2xl overflow-hidden border bg-card text-card-foreground transition-all ${
          plan.popular
            ? "shadow-xl scale-105 md:scale-110 z-10"
            : "hover:border-primary/20 hover:shadow-lg"
        }`}
      >
        {plan.popular && (
          <div className="absolute top-0 left-0 p-0 right-0 bg-primary py-2 text-primary-foreground text-xs font-semibold text-center tracking-wider">
            MOST POPULAR
          </div>
        )}

        <div className={`p-8 px-6 ${plan.popular ? "pt-12" : "pt-8"}`}>
          <h3 className="text-2xl font-bold">{plan.title}</h3>
          <p className="text-sm text-muted-foreground mt-2">
            {plan.description}
          </p>

          {/* Subscription Pricing */}
          <div className="my-6">
            <div className="flex items-baseline">
              <span className="text-4xl font-bold">
                $
                {billingCycle === "monthly"
                  ? plan.monthlyPrice
                  : plan.annualPrice}
              </span>
              <span className="text-muted-foreground ml-2 text-sm">
                /{billingCycle === "monthly" ? "mo" : "yr"}
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full py-6 font-medium text-base group border-primary/20 hover:bg-primary/5 hover:text-foreground transition-colors"
          >
            {plan.buttonLabel || "Get Started"}
            <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>

          {/* Lifetime Deal */}
          {plan.lifetimePriceId && (
            <div className="mt-10 mb-8 relative">
              <div className="absolute inset-0 bg-muted/50 rounded-xl"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span className="font-medium text-sm">Lifetime Deal</span>
                  </div>
                  <Badge className="font-normal text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800">
                    Limited time
                  </Badge>
                </div>
                <div className="flex items-baseline mb-2">
                  <span className="text-3xl font-bold">
                    ${plan.lifetimePrice}
                  </span>
                  <span className="text-muted-foreground ml-2 text-xs">one-time</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Pay once, use forever
                </p>
                <Button className="w-full py-5 font-medium text-base group bg-primary hover:bg-primary/90 text-primary-foreground border-0">
                  {plan.lifeTimeButtonLabel || "Purchase Now"}
                  <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Features */}
          <div className="mt-8 space-y-4">
            <p className="text-sm font-semibold">What&apos;s included:</p>
            <ul className="space-y-4">
              {plan.features.map((feature) => (
                <li key={feature} className="flex text-sm items-start">
                  <Check className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0 mr-3 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 max-w-6xl w-full mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>PRICING</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Get More Traffic Now
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Give your product the visibility it deserves
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <Tabs
          defaultValue="annual"
          className="w-full max-w-xs"
          onValueChange={(value) =>
            setBillingCycle(value as "monthly" | "annual")
          }
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="monthly">
              Monthly
            </TabsTrigger>
            <TabsTrigger value="annual">
              Annual
              <Badge
                variant="secondary"
                className="ml-2 font-normal bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
              >
                Save 20%
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Pricing Cards */}
      <div className="pt-24 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        {plans.map((plan, index) => (
          <PricingCard plan={plan} key={index} />
        ))}
      </div>
    </section>
  );
}
